import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';
import * as XLSX from 'xlsx';


export default function TeacherPanel() {
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacherEmail, setSelectedTeacherEmail] = useState('');
  const [activeData, setActiveData] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [viewDate, setViewDate] = useState(new Date()); // Default to Current Month
  const [loading, setLoading] = useState(true);
  const [homeworks, setHomeworks] = useState([]);
  const [showHomeworkModal, setShowHomeworkModal] = useState(false);
  const [homeworkFile, setHomeworkFile] = useState(null);
  const [homeworkForm, setHomeworkForm] = useState({ title: '', description: '', dueDate: '', batchIdStr: '' });
  const [assigning, setAssigning] = useState(false);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [selectedHomeworkSubmissions, setSelectedHomeworkSubmissions] = useState([]);
  const [viewingHomeworkTitle, setViewingHomeworkTitle] = useState('');
  
  // Download Attendance States
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadForm, setDownloadForm] = useState({
    teacherSelection: 'selected', // 'selected' or 'all'
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    format: 'xlsx'
  });

  const navigate = useNavigate();
  const { instituteId } = useParams();
  const [sessionUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const isTeacherView = sessionUser.role && sessionUser.role.toLowerCase().includes('teacher');
  
  // Weekly Timetable Navigation
  // Today's Live Timetable States
  const [timetableDate, setTimetableDate] = useState(new Date());

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_BASE_URL + '/api/teachers');
      if (res.data && res.data.length > 0) {
        setTeachers(res.data);
        
        // If logged in as a teacher, force selection to self
        if (isTeacherView && sessionUser.email) {
          setSelectedTeacherEmail(sessionUser.email);
        } else if (res.data[0].teacherEmail) {
          setSelectedTeacherEmail(res.data[0].teacherEmail);
        }
      } else {
        setTeachers([]);
      }
    } catch (err) {
      console.error("Failed to fetch teachers", err);
    } finally {
      setLoading(false);
    }
  }, [isTeacherView, sessionUser.email]);

  const fetchTeacherDetails = useCallback(async (email) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/teachers/email/${email}?t=${Date.now()}`);
      setActiveData(res.data);
    } catch (err) {
      console.error("Failed to fetch teacher details", err);
      toast("Error loading teacher profile.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHomeworks = useCallback(async (email) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/homework/teacher/${email}`);
      setHomeworks(res.data);
    } catch (err) {
      console.error("Failed to fetch homeworks", err);
    }
  }, []);

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm("Are you sure you want to delete this schedule slot?")) return;
    try {
        await axios.delete(`${API_BASE_URL}/api/batches/schedule/${id}`);
        toast("Schedule deleted successfully.");
        if (selectedTeacherEmail) fetchTeacherDetails(selectedTeacherEmail);
    } catch (err) {
        toast("Failed to delete schedule.");
    }
  };

  const getFilteredTimetable = () => {
    if (!activeData || !activeData.dynamicSchedule) return [];
    
    // Filter by date
    const dateStr = timetableDate.toISOString().split('T')[0];
    let filtered = activeData.dynamicSchedule.filter(s => s.date === dateStr);
    
    // Sort by time
    filtered.sort((a,b) => (a.startTime || "").localeCompare(b.startTime || ""));
    return filtered;
  };

  const handleStartClass = (cls) => {
    if (!cls.batchIdStr) {
      toast.error("Batch information missing.");
      return;
    }
    // Create a URL-safe room name (matching UpcomingClasses logic)
    const sanitizedBatch = String(cls.batchIdStr).replace(/[^a-zA-Z0-9]/g, '_');
    const sanitizedSubject = (cls.subject || 'Lecture').replace(/[^a-zA-Z0-9]/g, '_');
    const roomName = `PenSense_${sanitizedBatch}_${sanitizedSubject}`;
    
    // Use absolute navigation for 100% security
    const targetPath = `/${instituteId}/lms/live-class/${cls.batchIdStr}/${roomName}`;
    navigate(targetPath, { state: { from: window.location.pathname } });
  };

  const currentTimetableSlots = getFilteredTimetable();

  // Fetch all teacher summaries on mount
  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  useEffect(() => {
    if (selectedTeacherEmail) {
      fetchTeacherDetails(selectedTeacherEmail);
      fetchHomeworks(selectedTeacherEmail);
    } else {
      setActiveData(null);
      setHomeworks([]);
    }
  }, [selectedTeacherEmail, fetchTeacherDetails, fetchHomeworks]);



  const handleMarkAttendance = async (type) => {
    if (!selectedTeacherEmail) return;
    
    const markType = type === 'in' ? 'mark-in' : 'mark-out';
    const loadingToast = toast.info(`Accessing location and marking ${type === 'in' ? 'Check-In' : 'Check-Out'}...`, { autoClose: false });

    const performUpdate = async (lat = null, lon = null) => {
      try {
        const res = await axios.post(`${API_BASE_URL}/api/teachers/email/${selectedTeacherEmail}/attendance/${markType}`, {
          latitude: lat,
          longitude: lon
        });
        toast.dismiss(loadingToast);
        toast.success(`Successfully marked ${type === 'in' ? 'Check-In' : 'Check-Out'} at ${res.data[type === 'in' ? 'timeIn' : 'timeOut']}`);
        fetchTeacherDetails(selectedTeacherEmail);
      } catch (err) {
        toast.dismiss(loadingToast);
        const msg = err.response?.data?.message || err.message || "Failed to mark attendance. Check network connection.";
        toast.error(msg);
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          performUpdate(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error("Location error:", error);
          toast.dismiss(loadingToast);
          toast.error("Location access denied or unavailable. You must enable GPS and allow location access to mark attendance.");
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } else {
      toast.dismiss(loadingToast);
      toast.error("Your browser does not support geolocation. Please use a modern browser to mark attendance.");
    }
  };





  const handleAssignHomework = async (e) => {
    e.preventDefault();
    if (!homeworkFile) {
        toast("Please upload a PDF for the homework.");
        return;
    }
    setAssigning(true);
    try {
        // Step 1: Upload File
        const formData = new FormData();
        formData.append("file", homeworkFile);
        formData.append("title", homeworkForm.title);
        formData.append("subject", "Homework");
        formData.append("type", "HOMEWORK");
        
        const fileRes = await axios.post(`${API_BASE_URL}/api/files/upload`, formData);
        const fileId = fileRes.data.id;

        // Step 2: Create Homework
        const homeworkData = {
            ...homeworkForm,
            teacherEmail: selectedTeacherEmail,
            fileId: fileId
        };
        await axios.post(`${API_BASE_URL}/api/homework`, homeworkData);
        
        toast("Homework assigned successfully!");
        setShowHomeworkModal(false);
        setHomeworkFile(null);
        setHomeworkForm({ title: '', description: '', dueDate: '', batchIdStr: '' });
        fetchHomeworks(selectedTeacherEmail);
    } catch (err) {
        console.error(err);
        toast("Failed to assign homework.");
    } finally {
        setAssigning(false);
    }
  };

  const fetchSubmissions = async (homeworkId, title) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/homework/${homeworkId}/submissions`);
      setSelectedHomeworkSubmissions(res.data);
      setViewingHomeworkTitle(title);
      setShowSubmissionsModal(true);
    } catch (err) {
      console.error(err);
      toast("Failed to fetch submissions.");
    }
  };

  const handleDownloadAttendance = async () => {
    setIsDownloading(true);
    setDownloadProgress(0);
    try {
      let exportData = [];
      const { teacherSelection, startDate, endDate, format } = downloadForm;
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      if (teacherSelection === 'selected') {
        if (!selectedTeacherEmail || !activeData) {
          toast.error("Please select a teacher first.");
          setIsDownloading(false);
          return;
        }
        
        const filteredRecords = (activeData.attendanceRecords || []).filter(r => {
          const rDate = new Date(r.date);
          return rDate >= start && rDate <= end;
        });

        exportData = filteredRecords.map(r => ({
          'Date': r.date,
          'Teacher Name': activeData.name,
          'Email': activeData.teacherEmail,
          'Status': r.status,
          'Check-In': r.timeIn || '-',
          'Check-Out': r.timeOut || '-',
          'Location (In)': r.latitudeIn ? `${r.latitudeIn}, ${r.longitudeIn}` : '-',
          'Location (Out)': r.latitudeOut ? `${r.latitudeOut}, ${r.longitudeOut}` : '-',
          'Remarks': r.remarks || ''
        }));
      } else {
        // ALL Teachers
        const total = teachers.length;
        for (let i = 0; i < total; i++) {
          const teacher = teachers[i];
          setDownloadProgress(Math.round(((i) / total) * 100));
          
          try {
            const res = await axios.get(`${API_BASE_URL}/api/teachers/email/${teacher.teacherEmail}?t=${Date.now()}`);
            const data = res.data;
            const filtered = (data.attendanceRecords || []).filter(r => {
              const rDate = new Date(r.date);
              return rDate >= start && rDate <= end;
            });

            filtered.forEach(r => {
              exportData.push({
                'Date': r.date,
                'Teacher Name': data.name,
                'Email': data.teacherEmail,
                'Status': r.status,
                'Check-In': r.timeIn || '-',
                'Check-Out': r.timeOut || '-',
                'Location (In)': r.latitudeIn ? `${r.latitudeIn}, ${r.longitudeIn}` : '-',
                'Location (Out)': r.latitudeOut ? `${r.latitudeOut}, ${r.longitudeOut}` : '-',
                'Remarks': r.remarks || ''
              });
            });
          } catch (err) {
            console.error(`Failed to fetch attendance for ${teacher.teacherEmail}`, err);
          }
        }
        setDownloadProgress(100);
      }

      if (exportData.length === 0) {
        toast.info("No attendance records found for the selected criteria.");
        setIsDownloading(false);
        return;
      }

      // Sort by Date
      exportData.sort((a, b) => a.Date.localeCompare(b.Date));

      if (format === 'xlsx') {
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Attendance");
        XLSX.writeFile(wb, `Attendance_Report_${startDate}_to_${endDate}.xlsx`);
      } else {
        // CSV
        const headers = Object.keys(exportData[0]);
        const csvRows = [
          headers.join(','),
          ...exportData.map(row => headers.map(fieldName => JSON.stringify(row[fieldName] || '')).join(','))
        ];
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Attendance_Report_${startDate}_to_${endDate}.csv`;
        link.click();
      }

      toast.success("Attendance report downloaded successfully!");
      setShowDownloadModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate report.");
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  // Switch active data logic moved to useEffect watching selectedTeacherEmail





  const getDaysInMonth = () => {
    if (!activeData) return [];
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const numDays = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: numDays }, (_, i) => {
      const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
      let status = 'none'; // Future or unrecorded
      const record = activeData.attendanceRecords?.find(r => r.date === dayStr);
      if (record) {
          status = record.status;
      }
      return { day: i + 1, status };
    });
  };

  const getFirstDayOfMonth = () => {
      const year = viewDate.getFullYear();
      const month = viewDate.getMonth();
      const firstDay = new Date(year, month, 1).getDay();
      return firstDay === 0 ? 6 : firstDay - 1; // Make Monday = 0
  };

  const handlePrevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));

  // Compute live analytical percentage based on the currently viewed calendar month
  const safeData = activeData || {};
  const records = safeData.attendanceRecords || [];
  
  const today = new Date();
  const isCurrentMonth = viewDate.getMonth() === today.getMonth() && viewDate.getFullYear() === today.getFullYear();
  const isFutureMonth = viewDate.getFullYear() > today.getFullYear() || (viewDate.getFullYear() === today.getFullYear() && viewDate.getMonth() > today.getMonth());
  
  // Calculate days passed in the viewed month
  let daysPassedInMonth = 0;
  if (isCurrentMonth) {
    daysPassedInMonth = today.getDate(); // Days passed so far this month
  } else if (!isFutureMonth) {
    // Past month: Total days in that month
    daysPassedInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  }
  
  // Count specific 'present' markers isolated inside the viewed Month
  const presentDaysInMonth = records.filter(r => {
    if (r.status !== 'present') return false;
    const dateParts = r.date.split('-');
    if (dateParts.length < 3) return false;
    const rYear = parseInt(dateParts[0], 10);
    const rMonth = parseInt(dateParts[1], 10) - 1;
    return rYear === viewDate.getFullYear() && rMonth === viewDate.getMonth();
  }).length;
  
  const dynamicAttendancePercentage = (daysPassedInMonth === 0 || isFutureMonth) 
    ? "No Data" 
    : `${Math.round((presentDaysInMonth / daysPassedInMonth) * 100)}%`;

  const LIVE_METRICS = [
    { id: "attendance", label: "Attendance (Monthly)", value: dynamicAttendancePercentage, trend: "Click to View Heatmap", icon: "🕒", color: "text-blue-400", bg: "bg-blue-500/10", clickable: true },
    { id: "classes", label: "Classes Conducted (Month)", value: safeData.classesConducted || '0', trend: safeData.classesTrend || 'No data', icon: "👨‍🏫", color: "text-emerald-400", bg: "bg-emerald-500/10", clickable: false },
    { id: "leaves", label: "Leaves Balance", value: safeData.leavesBalance || '0', trend: safeData.leavesTrend || 'No data', icon: "🌴", color: "text-purple-400", bg: "bg-purple-500/10", clickable: false },
    { id: "doubts", label: "Doubts Pending", value: safeData.doubtsPending || '0', trend: safeData.doubtsTrend || 'No data', icon: "💬", color: "text-rose-400", bg: "bg-rose-500/10", clickable: false },
  ];

  const todayStr = new Date().toISOString().split('T')[0];
  const todayRecord = activeData?.attendanceRecords?.find(r => r.date === todayStr);
  const isCheckedIn = !!(todayRecord && todayRecord.timeIn);
  const isCheckedOut = !!(todayRecord && todayRecord.timeOut);

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Welcome Banner (Match LMS/StudentDashboard style) */}
      <div className="relative rounded-3xl bg-gradient-to-r from-indigo-900/40 via-purple-900/20 to-slate-900 border border-indigo-500/20 p-8 overflow-hidden">
        {/* Glow Effects */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full"></div>
        <div className="absolute bottom-0 left-32 w-48 h-48 bg-purple-500/10 blur-[60px] rounded-full"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex-1 max-w-2xl">
            <h1 className="text-3xl font-bold tracking-tight text-white mb-4 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
              Teacher Control Panel
            </h1>
            <div className="flex flex-col sm:flex-row gap-4">
              {(!isTeacherView || sessionUser.role === 'superadmin' || sessionUser.role === 'institute') && (
                <select 
                  value={selectedTeacherEmail}
                  onChange={(e) => setSelectedTeacherEmail(e.target.value)}
                  disabled={loading}
                  className="bg-slate-900/80 border border-indigo-500/20 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-indigo-500 flex-1 transition-colors shadow-inner font-medium disabled:opacity-50 disabled:cursor-wait"
                >
                  <option value="">
                    {loading && teachers.length === 0 
                      ? "Loading Teachers..." 
                      : teachers.length === 0 
                        ? "Add Teacher First" 
                        : "Select a Teacher..."}
                  </option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.teacherEmail}>{t.name} ({t.department})</option>
                  ))}
                </select>
              )}
              {isTeacherView && (
                <div className="flex-1 py-3 px-4 bg-indigo-500/10 border border-indigo-500/30 rounded-xl">
                  <span className="text-indigo-300 font-bold">Welcome back, {sessionUser.name || 'Professor'}!</span>
                  <p className="text-xs text-indigo-400/80 mt-1 italic">Authorized Teacher Terminal • {sessionUser.email}</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-3">
             <button 
                onClick={() => setShowDownloadModal(true)}
                className="group relative px-6 py-3 rounded-2xl bg-slate-900 hover:bg-indigo-600 text-slate-300 hover:text-white border border-indigo-500/20 hover:border-indigo-400 transition-all duration-300 shadow-lg flex items-center gap-3 overflow-hidden"
             >
               <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
               <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="relative z-10 group-hover:scale-110 transition-transform">
                 <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                 <polyline points="7 10 12 15 17 10"></polyline>
                 <line x1="12" y1="15" x2="12" y2="3"></line>
               </svg>
               <span className="relative z-10 font-bold text-sm tracking-wide">Download Attendance</span>
             </button>
          </div>
        </div>
      </div>

      {!activeData ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] border-2 border-dashed border-indigo-500/20 rounded-3xl bg-slate-900/20 mt-8 relative overflow-hidden">
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-500/10 blur-[100px] rounded-full"></div>
           <div className="relative z-10 flex flex-col items-center">
             <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center text-indigo-500/50 mb-6 shadow-inner ring-1 ring-white/5">
                {loading ? (
                   <div className="w-8 h-8 rounded-full border-2 border-indigo-500/30 border-t-indigo-500 animate-spin"></div>
                ) : (
                   <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
                )}
             </div>
             <h3 className="text-2xl font-bold text-slate-300 mb-2">
               {loading 
                 ? "Loading Teachers..." 
                 : teachers.length === 0 
                   ? "No Teachers Assigned" 
                   : "No Teacher Selected"}
             </h3>
             <p className="text-slate-500 text-center max-w-md">
               {loading 
                 ? "Please wait while we establish a connection with the Teacher Management Database." 
                 : teachers.length === 0 
                   ? "Your institution has zero registered faculty. Please use the 'User Management' module to register teachers and staff." 
                   : "Choose a teacher from the dropdown to view their exact realtime analytics, attendance, and schedules."}
             </p>
           </div>
        </div>
      ) : (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 mt-8">
      {/* Header Profile & Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 rounded-3xl bg-slate-900/60 border border-slate-800">
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 p-1 shadow-lg shadow-purple-500/20">
            <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center">
              <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">{activeData.initials}</span>
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{activeData.name}</h1>
            <p className="text-slate-400 text-sm">{activeData.title} • {activeData.department}</p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={() => setShowLeaveModal(true)}
            className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium transition-colors border border-slate-700 flex items-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
            Request Leave
          </button>
          <button 
            onClick={() => currentTimetableSlots.length > 0 ? handleStartClass(currentTimetableSlots[0]) : toast.info("No classes scheduled for this date.")}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium transition-all shadow-lg shadow-purple-500/20 flex items-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
            Join Live Class
          </button>
            <button 
            onClick={() => setShowHomeworkModal(true)}
            className="px-5 py-2.5 rounded-xl bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 font-medium transition-all border border-indigo-500/30 flex items-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>
            Assign Homework
          </button>
          
          <div className="flex gap-2 border-l border-slate-700 pl-4 ml-1">
            <button 
              onClick={() => handleMarkAttendance('in')}
              disabled={isCheckedIn}
              className={`px-5 py-2.5 rounded-xl font-bold transition-all shadow-lg flex items-center gap-2 ${
                isCheckedIn 
                ? "bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700" 
                : "bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/20"
              }`}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
              {isCheckedIn ? 'Checked In' : 'Check In'}
            </button>
            <button 
              onClick={() => handleMarkAttendance('out')}
              disabled={!isCheckedIn || isCheckedOut}
              className={`px-5 py-2.5 rounded-xl font-bold transition-all shadow-lg flex items-center gap-2 ${
                (!isCheckedIn || isCheckedOut)
                ? "bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700" 
                : "bg-rose-500 hover:bg-rose-600 text-white shadow-rose-500/20"
              }`}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              {isCheckedOut ? 'Checked Out' : 'Check Out'}
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Array */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {LIVE_METRICS.map((metric, idx) => (
          <div 
            key={idx} 
            onClick={() => metric.clickable && setShowAttendanceModal(true)}
            className={`rounded-2xl border border-slate-800 bg-slate-900/40 p-6 flex items-start gap-4 transition-colors ${metric.clickable ? 'cursor-pointer hover:bg-blue-900/20 hover:border-blue-500/50' : 'hover:bg-slate-800/40'}`}
          >
            <div className={`w-12 h-12 rounded-xl ${metric.bg} flex items-center justify-center text-2xl shrink-0 ${metric.clickable ? 'animate-pulse' : ''}`}>
              {metric.icon}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-400">{metric.label}</p>
              <div className="flex items-end gap-2 mt-1">
                <span className={`text-2xl font-bold ${metric.color}`}>{metric.value}</span>
              </div>
              <span className={`text-xs font-semibold mt-1 block ${metric.clickable ? 'text-blue-400' : 'text-slate-500'}`}>{metric.trend}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Schedule & Batches */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* TODAY'S LIVE TIMETABLE (Horizontal List) */}
          <section className="space-y-4">
            
            {/* Header Area */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => {
                    const d = new Date(timetableDate);
                    d.setDate(d.getDate() - 1);
                    setTimetableDate(d);
                  }}
                  className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 transition-all border border-slate-700/50 shadow-lg active:scale-90"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </button>
                <h2 className="text-xl font-bold text-slate-100 flex items-center gap-3">
                  {timetableDate.toDateString() === new Date().toDateString() ? "Today's Schedule" : `Schedule for ${timetableDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                </h2>
                <button 
                  onClick={() => {
                    const d = new Date(timetableDate);
                    d.setDate(d.getDate() + 1);
                    setTimetableDate(d);
                  }}
                  className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 transition-all border border-slate-700/50 shadow-lg active:scale-90"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
              </div>
            </div>

            {/* List View */}
            <div className="space-y-4">
              {currentTimetableSlots.length > 0 ? currentTimetableSlots.map((cls, idx) => (
                <div key={idx} className="bg-slate-900/40 border border-slate-800 rounded-[24px] p-4 flex items-center gap-6 group hover:border-emerald-500/30 transition-all duration-300 relative overflow-hidden backdrop-blur-sm">
                  {/* Glass Background Micro-decoration */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-[40px] -mr-16 -mt-16 rounded-full group-hover:bg-emerald-500/10 transition-colors"></div>
                  
                  {/* Time Box */}
                  <div className="flex-shrink-0 w-28 h-20 rounded-2xl bg-slate-950 border border-slate-800/60 flex flex-col items-center justify-center gap-1 shadow-inner relative z-10">
                     <span className="text-xl font-black text-slate-100 tracking-tight">{cls.startTime}</span>
                     <span className="text-[10px] uppercase font-black text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded tracking-widest">{`to ${cls.endTime}`}</span>
                  </div>

                  {/* Main Details */}
                  <div className="flex-1 relative z-10">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] font-black uppercase tracking-[2px] text-slate-500">{cls.subject}</span>
                      <span className="text-[9px] font-bold text-amber-500 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full uppercase tracking-wider">{cls.batchIdStr}</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-100 group-hover:text-white transition-colors">
                      {cls.topic || "Scheduled Lecture"}
                    </h3>
                  </div>

                  {/* Teacher Info */}
                  <div className="hidden md:flex flex-col items-end gap-0.5 text-right relative z-10 mr-4">
                    <span className="text-sm font-bold text-slate-300">{cls.teacherEmail}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-4 relative z-10">
                    <button 
                       onClick={() => handleStartClass(cls)}
                       className="px-6 py-2.5 rounded-2xl bg-emerald-950/40 hover:bg-emerald-500 text-emerald-400 hover:text-white border border-emerald-500/30 hover:border-emerald-400 font-black text-sm uppercase tracking-widest transition-all shadow-lg active:scale-95"
                    >
                      Start Class
                    </button>
                    <button 
                       onClick={() => handleDeleteSchedule(cls.id)}
                       className="p-2 text-slate-600 hover:text-rose-400 transition-colors bg-slate-950/20 rounded-lg border border-transparent hover:border-rose-500/20"
                    >
                       <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                    </button>
                  </div>
                </div>
              )) : (
                <div className="py-20 flex flex-col items-center justify-center bg-slate-900/40 border border-slate-800 rounded-[32px] border-dashed">
                   <div className="p-4 rounded-full bg-slate-800/50 mb-4">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="slate-600" strokeWidth="1.5"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                   </div>
                   <p className="text-lg font-bold text-slate-500">No classes scheduled for this day.</p>
                   <p className="text-sm text-slate-600">Enjoy your break or check other dates.</p>
                </div>
              )}
            </div>
          </section>

          {/* Homework Management Section */}
          <section className="rounded-3xl border border-indigo-500/20 bg-gradient-to-br from-slate-900/80 to-slate-900/40 p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/5 blur-[40px] rounded-full"></div>
            
            <div className="flex items-center justify-between mb-6 relative z-10">
              <h2 className="text-lg font-semibold text-indigo-200 flex items-center gap-2">
                <span className="text-indigo-500">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                </span>
                Homework Management
              </h2>
              <button 
                onClick={() => setShowHomeworkModal(true)}
                className="text-xs font-bold text-indigo-400 hover:text-indigo-300 transition-colors bg-indigo-500/10 px-3 py-1.5 rounded-lg border border-indigo-500/20"
              >
                + Create New
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
              {homeworks.length > 0 ? homeworks.map((hw, idx) => (
                <div key={idx} className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/50 hover:bg-slate-800/60 transition-all group">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-semibold text-slate-200 group-hover:text-indigo-300 transition-colors">{hw.homework.title}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{hw.homework.batchIdStr} • Due: {hw.homework.dueDate}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-bold bg-slate-900 px-2 py-1 rounded text-emerald-400 ring-1 ring-emerald-500/20">
                        {hw.submissionCount}/{hw.assignedCount || 0} Submissions
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-slate-700/50 flex items-center justify-between text-xs">
                    <div className="flex gap-3">
                      {hw.attachment && (
                        <a 
                          href={`${API_BASE_URL}/api/files/download/${hw.attachment.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-400 hover:text-indigo-300 font-medium flex items-center gap-1"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                          View PDF
                        </a>
                      )}
                    </div>
                    <button 
                      onClick={() => fetchSubmissions(hw.homework.id, hw.homework.title)}
                      className="text-slate-400 hover:text-white transition-colors flex items-center gap-1"
                    >
                      View Students
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                    </button>
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-slate-500 text-sm py-4 italic">No homework assigned yet. Click 'Create New' to start.</div>
              )}
            </div>
          </section>

          {/* Leave Management & History */}
          <section className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-200">Leave History</h2>
              <button 
                onClick={() => setShowLeaveModal(true)}
                className="text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors"
              >
                Apply for Leave
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                    <th className="pb-3 font-semibold">Date Applied</th>
                    <th className="pb-3 font-semibold">Leave Type</th>
                    <th className="pb-3 font-semibold">Duration</th>
                    <th className="pb-3 font-semibold text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {activeData.leaveHistory && activeData.leaveHistory.length > 0 ? activeData.leaveHistory.map((leave, idx) => (
                    <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-4 text-sm font-medium text-slate-200">{leave.dateApplied}</td>
                      <td className="py-4 text-sm text-slate-400">{leave.type}</td>
                      <td className="py-4 text-sm text-slate-400">{leave.duration}</td>
                      <td className="py-4 text-right">
                        <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                          leave.status === 'Approved' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 
                          'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        }`}>
                          {leave.status}
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="4" className="py-4 text-slate-400">No leave requests found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Right Column: Assigned Batches */}
        <div className="space-y-8">
          
          <section className="rounded-3xl border border-fuchsia-500/20 bg-gradient-to-br from-slate-900/80 to-slate-900/40 p-6 relative overflow-hidden h-full">
            <div className="absolute top-0 right-0 w-48 h-48 bg-fuchsia-500/5 blur-[40px] rounded-full"></div>
            
            <div className="flex items-center justify-between mb-6 relative z-10">
              <h2 className="text-lg font-semibold text-fuchsia-200 flex items-center gap-2">
                <span className="text-fuchsia-500">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                </span>
                Assigned Batches
              </h2>
            </div>
            
            <div className="space-y-4 relative z-10">
              {activeData.assignedBatches && activeData.assignedBatches.length > 0 ? activeData.assignedBatches.map((batch, idx) => (
                <div key={idx} className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/50 hover:bg-slate-800/60 transition-colors">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-slate-200">{batch.name}</h3>
                    <span className="text-xs font-mono bg-slate-900 px-2 py-1 rounded text-slate-400">
                      {batch.enrolledCount || 0} Students
                    </span>
                  </div>
                  
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                      <span>Syllabus Progress</span>
                      <span className="text-fuchsia-400 font-medium">{batch.progress}</span>
                    </div>
                    <div className="w-full bg-slate-900 rounded-full h-1.5">
                      <div className="bg-gradient-to-r from-fuchsia-500 to-purple-500 h-1.5 rounded-full" style={{ width: batch.progress }}></div>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-slate-700/50 flex items-center justify-between text-xs">
                    <span className="text-slate-500">Next Class:</span>
                    <span className="font-medium text-slate-300">{batch.nextClass}</span>
                  </div>
                </div>
              )) : (
                <div className="text-slate-400 font-medium py-4">No batches currently assigned.</div>
              )}
            </div>

            <button className="w-full mt-6 py-2.5 bg-slate-800/50 hover:bg-slate-800 text-slate-300 font-medium rounded-xl border border-slate-700 transition-colors relative z-10">
              View All Batches Directory
            </button>
          </section>

          

        </div>
      </div>

      {/* Attendance Heatmap Modal */}
      {showAttendanceModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-2xl w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-blue-400"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg></span>
                  Monthly Attendance Map
                </h2>
                <div className="flex items-center gap-3 mt-2">
                    <button onClick={handlePrevMonth} className="p-1.5 hover:bg-slate-800 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 transition-colors">
                       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                    </button>
                    <p className="text-slate-200 text-sm font-bold min-w-[120px] text-center bg-slate-800 py-1.5 px-3 rounded-lg shadow-inner">
                      {viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </p>
                    <button onClick={handleNextMonth} className="p-1.5 hover:bg-slate-800 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 transition-colors">
                       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                    </button>
                    <span className="text-slate-500 font-medium text-xs ml-3 border-l border-slate-700 pl-3">Teacher: {activeData.name}</span>
                </div>
              </div>
              <button onClick={() => setShowAttendanceModal(false)} className="text-slate-400 hover:text-white p-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            {/* Heatmap Grid */}
            <div className="bg-slate-950 border border-slate-800 rounded-xl p-6">
              <div className="grid grid-cols-7 gap-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-center text-xs font-semibold text-slate-500 mb-2">{day}</div>
                ))}
                
                {/* Dynamically blank spaces mapping the start day */}
                {Array.from({ length: getFirstDayOfMonth() }).map((_, i) => <div key={`empty-${i}`} />)}
                
                {getDaysInMonth().map((d, i) => {
                  let colorClass = "bg-slate-800/50 border-slate-700/50"; // none
                  if (d.status === 'present') colorClass = "bg-emerald-500/20 border-emerald-500/50 text-emerald-300 shadow-[0_0_10px_rgba(16,185,129,0.2)]"; // present
                  if (d.status === 'absent') colorClass = "bg-rose-500/20 border-rose-500/50 text-rose-300 shadow-[0_0_10px_rgba(244,63,94,0.2)]"; // absent
                  
                  return (
                    <div 
                      key={i} 
                      className={`relative aspect-square rounded-lg border flex items-center justify-center font-medium text-sm transition-all hover:scale-105 cursor-default ${colorClass} group`}
                    >
                      {d.day}
                      {d.status !== 'none' && (
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 border border-slate-700">
                          {d.status === 'present' ? 'Present' : 'Absent'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div className="flex items-center justify-end gap-4 mt-6 text-sm">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-500/20 border border-emerald-500/50"></div> <span className="text-slate-400">Present</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-rose-500/20 border border-rose-500/50"></div> <span className="text-slate-400">Absent</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-slate-800/50 border border-slate-700/50"></div> <span className="text-slate-400">No Record</span></div>
              </div>
            </div>

            <button onClick={() => setShowAttendanceModal(false)} className="w-full mt-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold transition-all">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Download Attendance Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-slate-900 border border-slate-800 rounded-[32px] p-8 max-w-lg w-full shadow-2xl animate-in zoom-in-95 duration-300 relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 blur-[80px] -mr-32 -mt-32 rounded-full"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 blur-[80px] -ml-32 -mb-32 rounded-full"></div>

            <div className="relative z-10">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-black text-white tracking-tight flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-400">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                    </div>
                    Download Attendance
                  </h2>
                  <p className="text-slate-400 text-sm mt-1 font-medium">Export faculty attendance logs to spreadsheet.</p>
                </div>
                <button 
                  onClick={() => !isDownloading && setShowDownloadModal(false)} 
                  className="text-slate-500 hover:text-white p-2 hover:bg-slate-800 rounded-full transition-all"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Teacher Selection */}
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Target Faculty</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button 
                      onClick={() => setDownloadForm({...downloadForm, teacherSelection: 'selected'})}
                      className={`py-3 px-4 rounded-2xl border-2 transition-all flex items-center justify-center gap-2 font-bold text-sm ${downloadForm.teacherSelection === 'selected' ? 'bg-indigo-500/10 border-indigo-500 text-indigo-400 shadow-lg shadow-indigo-500/10' : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'}`}
                    >
                      Selected Teacher
                    </button>
                    <button 
                      onClick={() => setDownloadForm({...downloadForm, teacherSelection: 'all'})}
                      className={`py-3 px-4 rounded-2xl border-2 transition-all flex items-center justify-center gap-2 font-bold text-sm ${downloadForm.teacherSelection === 'all' ? 'bg-purple-500/10 border-purple-500 text-purple-400 shadow-lg shadow-purple-500/10' : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'}`}
                    >
                      All Teachers
                    </button>
                  </div>
                </div>

                {/* Period Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Start Date</label>
                    <input 
                      type="date" 
                      value={downloadForm.startDate}
                      onChange={(e) => setDownloadForm({...downloadForm, startDate: e.target.value})}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-indigo-500 outline-none transition-all font-bold text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">End Date</label>
                    <input 
                      type="date" 
                      value={downloadForm.endDate}
                      onChange={(e) => setDownloadForm({...downloadForm, endDate: e.target.value})}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-indigo-500 outline-none transition-all font-bold text-sm"
                    />
                  </div>
                </div>

                {/* Format Selection */}
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Export Format</label>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input 
                        type="radio" 
                        name="format" 
                        value="xlsx" 
                        checked={downloadForm.format === 'xlsx'}
                        onChange={(e) => setDownloadForm({...downloadForm, format: e.target.value})}
                        className="w-4 h-4 text-indigo-500 bg-slate-950 border-slate-700 focus:ring-indigo-500"
                      />
                      <span className={`text-sm font-bold ${downloadForm.format === 'xlsx' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'}`}>Microsoft Excel (.xlsx)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input 
                        type="radio" 
                        name="format" 
                        value="csv" 
                        checked={downloadForm.format === 'csv'}
                        onChange={(e) => setDownloadForm({...downloadForm, format: e.target.value})}
                        className="w-4 h-4 text-indigo-500 bg-slate-950 border-slate-700 focus:ring-indigo-500"
                      />
                      <span className={`text-sm font-bold ${downloadForm.format === 'csv' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'}`}>CSV Spreadsheet (.csv)</span>
                    </label>
                  </div>
                </div>

                {/* Progress Bar (Visible during multi-fetch) */}
                {isDownloading && downloadForm.teacherSelection === 'all' && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                    <div className="flex justify-between text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                      <span>Fetching Faculty Data...</span>
                      <span>{downloadProgress}%</span>
                    </div>
                    <div className="w-full bg-slate-950 rounded-full h-1.5 overflow-hidden">
                      <div 
                        className="bg-indigo-500 h-full transition-all duration-300 shadow-[0_0_10px_rgba(99,102,241,0.5)]" 
                        style={{ width: `${downloadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                <button 
                  onClick={handleDownloadAttendance}
                  disabled={isDownloading}
                  className="w-full py-4 rounded-2xl bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black text-lg transition-all shadow-xl shadow-indigo-500/20 flex justify-center items-center gap-3 active:scale-[0.98]"
                >
                  {isDownloading ? (
                    <>
                      <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                      Generate Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leave Application Modal */}
      {showLeaveModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Apply for Leave</h2>
              <button onClick={() => setShowLeaveModal(false)} className="text-slate-400 hover:text-white p-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); setShowLeaveModal(false); toast("Leave request submitted successfully to admin!"); }}>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1.5">Leave Type</label>
                <select className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all">
                  <option>Casual Leave</option>
                  <option>Medical / Sick Leave</option>
                  <option>Unpaid Leave</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1.5">Start Date</label>
                  <input type="date" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-purple-500 transition-all [color-scheme:dark]" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1.5">End Date</label>
                  <input type="date" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-purple-500 transition-all [color-scheme:dark]" required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1.5">Reason</label>
                <textarea rows="3" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-purple-500 transition-all resize-none" placeholder="Brief reason for your absence..."></textarea>
              </div>

              <button type="submit" className="w-full py-3 rounded-xl bg-purple-500 hover:bg-purple-600 text-white font-bold text-lg transition-all shadow-lg shadow-purple-500/20">
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
    )}



      {/* Homework Creation Modal */}
      {showHomeworkModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <span className="text-indigo-400"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg></span>
                  Assign New Homework
                </h2>
                <p className="text-sm text-slate-400 mt-1">Upload PDF and select batch for assignment.</p>
              </div>
              <button onClick={() => setShowHomeworkModal(false)} className="text-slate-400 hover:text-white p-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            <form className="space-y-4" onSubmit={handleAssignHomework}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                   <label className="block text-sm font-medium text-slate-400 mb-1.5">Title</label>
                   <input 
                     type="text" 
                     className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-indigo-500 transition-all outline-none"
                     placeholder="e.g. Kinematics Practice Set"
                     value={homeworkForm.title}
                     onChange={e => setHomeworkForm({...homeworkForm, title: e.target.value})}
                     required
                   />
                </div>
                <div>
                   <label className="block text-sm font-medium text-slate-400 mb-1.5">Batch</label>
                   <select 
                     className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-indigo-500 transition-all outline-none"
                     value={homeworkForm.batchIdStr}
                     onChange={e => setHomeworkForm({...homeworkForm, batchIdStr: e.target.value})}
                     required
                   >
                     <option value="">Select Batch...</option>
                     {activeData?.assignedBatches?.map(b => (
                       <option key={b.batchIdStr} value={b.batchIdStr}>{b.name}</option>
                     ))}
                   </select>
                </div>
                <div>
                   <label className="block text-sm font-medium text-slate-400 mb-1.5">Due Date</label>
                   <input 
                     type="date" 
                     className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-indigo-500 transition-all outline-none [color-scheme:dark]"
                     value={homeworkForm.dueDate}
                     onChange={e => setHomeworkForm({...homeworkForm, dueDate: e.target.value})}
                     required
                   />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1.5">Description</label>
                <textarea 
                  rows="3" 
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 focus:border-indigo-500 transition-all resize-none outline-none"
                  placeholder="Brief instructions for students..."
                  value={homeworkForm.description}
                  onChange={e => setHomeworkForm({...homeworkForm, description: e.target.value})}
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1.5">Upload Homework PDF</label>
                <div className="relative group cursor-pointer h-24 w-full rounded-2xl border-2 border-dashed border-slate-700 hover:border-indigo-500 bg-slate-950/50 hover:bg-indigo-500/5 transition-colors flex flex-col items-center justify-center text-center p-4">
                  <input 
                    type="file" 
                    accept=".pdf" 
                    onChange={(e) => setHomeworkFile(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                  />
                  {!homeworkFile ? (
                    <p className="text-xs text-slate-500 group-hover:text-indigo-400 transition-colors">Drag & drop PDF here or click</p>
                  ) : (
                    <p className="text-xs text-emerald-400 font-medium truncate max-w-full">{homeworkFile.name}</p>
                  )}
                </div>
              </div>

              <button 
                type="submit" 
                disabled={assigning}
                className="w-full py-3.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white font-bold text-lg transition-all shadow-lg shadow-indigo-500/20 flex justify-center items-center gap-2"
              >
                {assigning ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : 'Assign Homework'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Homework Submissions Viewer Modal */}
      {showSubmissionsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-2xl w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">Submissions: {viewingHomeworkTitle}</h2>
                <p className="text-sm text-slate-400 mt-1">Total {selectedHomeworkSubmissions.length} students have submitted.</p>
              </div>
              <button onClick={() => setShowSubmissionsModal(false)} className="text-slate-400 hover:text-white p-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            
            <div className="max-h-[400px] overflow-y-auto space-y-3 custom-scrollbar pr-2">
              {selectedHomeworkSubmissions.length > 0 ? selectedHomeworkSubmissions.map((sub, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-slate-950/50 border border-slate-800 flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-slate-200 text-sm">{sub.submission.studentEmail}</h3>
                    <p className="text-[10px] text-slate-500 mt-1">Submitted on: {new Date(sub.submission.submissionDate).toLocaleString()}</p>
                  </div>
                  {sub.file && (
                    <a 
                      href={`${API_BASE_URL}/api/files/download/${sub.file.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-xs font-bold border border-emerald-500/20 transition-all flex items-center gap-2"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                      Download PDF
                    </a>
                  )}
                </div>
              )) : (
                <div className="py-8 text-center text-slate-500 italic">No submissions yet for this homework.</div>
              )}
            </div>

            <button onClick={() => setShowSubmissionsModal(false)} className="w-full mt-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold transition-all">
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
